import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import DotboxLogo from '@/components/common/DotboxLogo';
import {
  Eye, EyeOff, Loader2, ArrowLeft, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const formatApiError = (e, fallback = 'Something went wrong') => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return fallback;
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [tokenState, setTokenState] = useState('unknown'); // 'valid' | 'invalid' | 'expired' | 'used'
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setTokenState('invalid');
        setValidating(false);
        return;
      }
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password/validate`,
          { params: { token } },
        );
        if (r.data?.valid) {
          setTokenState('valid');
        } else {
          setTokenState(r.data?.reason || 'invalid');
        }
      } catch {
        setTokenState('invalid');
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSubmitted(true);
      toast.success('Password updated. You can sign in now.');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not reset password'));
    } finally {
      setLoading(false);
    }
  };

  const renderInvalid = () => {
    const messages = {
      invalid: {
        title: 'Reset link not valid',
        body: 'This reset link is invalid or has been tampered with. Request a new one and try again.',
      },
      expired: {
        title: 'Reset link expired',
        body: 'This reset link has expired. Reset links are valid for 60 minutes. Please request a new one.',
      },
      used: {
        title: 'Reset link already used',
        body: 'This reset link has already been used. If you didn\'t change your password, request a new link and contact support.',
      },
    };
    const m = messages[tokenState] || messages.invalid;
    return (
      <Card data-testid="reset-password-invalid-card">
        <CardHeader>
          <div className="flex items-center gap-2 text-white">
            <AlertTriangle className="h-6 w-6 text-amber-400" />
            <CardTitle>{m.title}</CardTitle>
          </div>
          <CardDescription>{m.body}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Link
            to="/forgot-password"
            className="text-sm font-semibold text-red-400 hover:text-red-300"
            data-testid="reset-password-request-new"
          >
            Request a new link
          </Link>
          <span className="text-slate-500">•</span>
          <Link
            to="/login"
            className="text-sm text-slate-300 hover:text-slate-100 inline-flex items-center gap-1"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </Link>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A1024] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <DotboxLogo className="h-10 mx-auto mb-4" />
        </div>
        {validating && (
          <Card data-testid="reset-password-validating">
            <CardContent className="py-10 text-center text-slate-300">
              <Loader2 className="h-6 w-6 animate-spin text-red-400 mx-auto mb-3" />
              Verifying your reset link...
            </CardContent>
          </Card>
        )}

        {!validating && tokenState !== 'valid' && renderInvalid()}

        {!validating && tokenState === 'valid' && !submitted && (
          <Card data-testid="reset-password-card">
            <CardHeader>
              <CardTitle className="text-white">Set a new password</CardTitle>
              <CardDescription>
                Choose a strong password you don&apos;t use anywhere else.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      className="h-11 pr-10"
                      data-testid="reset-password-new-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={loading}
                    className="h-11"
                    data-testid="reset-password-confirm-input"
                  />
                  {confirm && password !== confirm && (
                    <p className="text-xs text-amber-400" data-testid="reset-password-mismatch">
                      Passwords don&apos;t match
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading || !password || password !== confirm}
                  className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C]"
                  data-testid="reset-password-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {submitted && (
          <Card data-testid="reset-password-success-card">
            <CardHeader>
              <div className="flex items-center gap-2 text-white">
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                <CardTitle>Password updated</CardTitle>
              </div>
              <CardDescription>
                Redirecting you to sign in...
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link
                to="/login"
                className="text-sm text-red-400 hover:text-red-300 inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Go to sign in
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
